/**
 * 字段组拖拽Hook
 * 职责: 处理字段组的拖拽排序逻辑
 */

import type { IFieldGroup } from '@pharmsnap/pharmsnap-web/composable/useDrugFinderExpertSearch'
import { computed, ref, Ref } from '@vue/composition-api'

// 拖拽状态接口
interface IDragState {
  draggedIndex: number | null
  dragOverIndex: number | null
  dropPosition: 'above' | 'below' | null
  isDragging: boolean
  isDraggable: boolean
}

export function useFieldGroupDrag(fieldGroups: Ref<IFieldGroup[]>) {
  // 拖拽状态聚合管理
  const dragState = ref<IDragState>({
    draggedIndex: null,
    dragOverIndex: null,
    dropPosition: null,
    isDragging: false,
    isDraggable: false,
  })

  // ============ 计算属性 ============
  const isDragging = computed(() => dragState.value.isDragging)
  const isDraggable = computed(() => dragState.value.isDraggable)
  const draggedIndex = computed(() => dragState.value.draggedIndex)
  const dragOverIndex = computed(() => dragState.value.dragOverIndex)
  const dropPosition = computed(() => dragState.value.dropPosition)

  // ============ 拖拽处理函数 ============

  /**
   * 开始拖拽
   */
  const handleDragStart = (e: DragEvent, index: number): void => {
    dragState.value.draggedIndex = index

    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/html', '')
    }
  }

  /**
   * 拖拽经过
   */
  const handleDragOver = (e: DragEvent, index: number): void => {
    e.preventDefault()

    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move'
    }

    const currentDraggedIndex = dragState.value.draggedIndex
    if (currentDraggedIndex === null || currentDraggedIndex === index) return

    // 计算插入位置
    const targetElement = e.currentTarget as HTMLElement
    const rect = targetElement.getBoundingClientRect()
    const mouseY = e.clientY
    const elementMiddle = rect.top + rect.height / 2
    const position: 'above' | 'below' = mouseY < elementMiddle ? 'above' : 'below'

    // 更新位置指示
    if (dragState.value.dragOverIndex !== index || dragState.value.dropPosition !== position) {
      dragState.value.dropPosition = position
    }

    // 执行移动
    const draggedItem = fieldGroups.value[currentDraggedIndex]
    const newGroups = [...fieldGroups.value]

    // 移除拖拽的项目
    newGroups.splice(currentDraggedIndex, 1)
    // 在新位置插入
    const insertIndex = currentDraggedIndex < index ? currentDraggedIndex + 1 : index
    newGroups.splice(insertIndex, 0, draggedItem)

    dragState.value.isDragging = true

    // 更新ID
    if (currentDraggedIndex !== index) {
      fieldGroups.value = newGroups.map((group, idx) => ({
        ...group,
        id: (idx + 1).toString(),
      }))

      dragState.value.dragOverIndex = index
      dragState.value.draggedIndex = insertIndex
    }
  }

  /**
   * 拖拽离开
   */
  const handleDragLeave = (): void => {
    // 可以在这里添加视觉反馈的清除逻辑
  }

  /**
   * 放下
   */
  const handleDrop = (e: DragEvent, dropIndex: number): void => {
    e.preventDefault()
    resetDragState()
  }

  /**
   * 拖拽结束
   */
  const handleDragEnd = (): void => {
    resetDragState()
  }

  /**
   * 重置拖拽状态
   */
  const resetDragState = (): void => {
    dragState.value = {
      draggedIndex: null,
      dragOverIndex: null,
      dropPosition: null,
      isDragging: false,
      isDraggable: false,
    }
  }

  /**
   * 设置可拖拽状态
   */
  const setDraggable = (draggable: boolean): void => {
    dragState.value.isDraggable = draggable
  }

  return {
    // 状态
    dragState,
    isDragging,
    isDraggable,
    draggedIndex,
    dragOverIndex,
    dropPosition,

    // 方法
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    setDraggable,
    resetDragState,
  }
}

export type UseFieldGroupDragReturn = ReturnType<typeof useFieldGroupDrag>
